import { useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography, CircularProgress } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { apiData } from "../services/api_service";
import AttachmentPreviewDialog, { attachmentKind } from "./AttachmentPreviewDialog";

// Payment proof upload for the Payment & Notes step (Invoice/CDN/Proforma
// forms). Three states, matching any standard file-upload pattern:
//   1. Empty      — dashed "Upload" button
//   2. Uploading  — inline spinner, disabled
//   3. Uploaded   — filename + type icon, "View" and "Remove" actions
//
// Controlled: the parent (formik) owns url/name/uploading — this component
// only drives them via the callbacks, same shape as the old inline Button.
const fileIconFor = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return PictureAsPdfIcon;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return ImageIcon;
  return InsertDriveFileIcon;
};

export default function PaymentAttachmentUpload({
  url,
  name,
  uploading,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  onRemove,
  uploadApi,
  accept = "image/*,.pdf",
  readOnly = false,
  height = 56,
  label = "Upload Payment Attachment",
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Let the same <input> be used to pick the same file twice in a row.
    e.target.value = "";
    if (!file) return;

    onUploadStart?.();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiData("post", uploadApi, fd);
      if (res?.success && res.data) {
        onUploadSuccess?.({ url: res.data, name: file.name });
      } else {
        onUploadError?.("Attachment upload failed");
      }
    } catch (err) {
      console.error("Attachment upload failed:", err);
      onUploadError?.("Attachment upload failed");
    }
  };

  if (uploading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          height,
          px: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      >
        <CircularProgress size={18} thickness={5} />
        <Typography variant="body2" color="text.secondary" noWrap>
          Uploading{name ? ` "${name}"` : ""}…
        </Typography>
      </Box>
    );
  }

  if (url) {
    const FileIcon = fileIconFor(name || url);
    return (
      <>
      <AttachmentPreviewDialog open={previewOpen} url={url} name={name} onClose={() => setPreviewOpen(false)} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height,
          pl: 1.5,
          pr: 0.75,
          border: "1px solid",
          borderColor: "success.light",
          borderRadius: 1,
          bgcolor: "success.50",
        }}
      >
        {attachmentKind(name || url) === "image" ? (
          <Box
            component="img"
            src={url}
            alt=""
            onClick={() => setPreviewOpen(true)}
            sx={{ width: 34, height: 34, objectFit: "cover", borderRadius: 0.75, border: "1px solid", borderColor: "divider", cursor: "zoom-in", flexShrink: 0 }}
          />
        ) : (
          <FileIcon fontSize="small" sx={{ color: "success.main", flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
          title={name || "Payment attachment"}
        >
          {name || "Payment attachment"}
        </Typography>
        <Tooltip title="Preview" arrow>
          <IconButton size="small" onClick={() => setPreviewOpen(true)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!readOnly && (
          <Tooltip title="Remove attachment" arrow>
            <IconButton size="small" onClick={onRemove}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      </>
    );
  }

  if (readOnly) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No attachment available
      </Typography>
    );
  }

  return (
    <Button
      variant="outlined"
      component="label"
      fullWidth
      startIcon={<UploadFileIcon />}
      sx={{
        height,
        borderStyle: "dashed",
        justifyContent: "flex-start",
        px: 2,
        fontWeight: 500,
        textTransform: "none",
      }}
    >
      {label}
      <input type="file" hidden accept={accept} onChange={handleFileChange} />
    </Button>
  );
}
